import { useCallback, useMemo, useState } from 'react'
import {
    findCreativesPlan,
    findLicensePlan,
    findSupportPlan,
} from '@/pages/users/Subscriptions/planResolver'
import {
    SUBSCRIPTION_LICENSE,
    SUBSCRIPTION_SUPPORT,
    SUBSCRIPTION_CREATIVE,
} from '@/pages/users/Subscriptions/constants'

const DEFAULT_CYCLE = 'monthly'
const DEFAULT_TIER = 'start'

const DEFAULT_BILLING_CYCLES = {
    [SUBSCRIPTION_LICENSE]: DEFAULT_CYCLE,
    [SUBSCRIPTION_SUPPORT]: DEFAULT_CYCLE,
}

export const useSubscriptionPlanSelection = ({
    plans,
    initialBillingCycles = DEFAULT_BILLING_CYCLES,
    initialCreativeTier = DEFAULT_TIER,
    initialCreativeFrequency = DEFAULT_CYCLE,
} = {}) => {
    const [billingCycles, setBillingCycles] = useState(() => ({
        ...DEFAULT_BILLING_CYCLES,
        ...(initialBillingCycles || {}),
    }))
    const [creativeTier, setCreativeTier] = useState(() => initialCreativeTier || DEFAULT_TIER)
    const [creativeFrequency, setCreativeFrequency] = useState(
        () => initialCreativeFrequency || DEFAULT_CYCLE
    )

    const licenseCycle = billingCycles[SUBSCRIPTION_LICENSE]
    const supportCycle = billingCycles[SUBSCRIPTION_SUPPORT]

    const licensePlan = useMemo(
        () => findLicensePlan(plans ?? [], licenseCycle),
        [plans, licenseCycle]
    )
    const supportPlan = useMemo(
        () => findSupportPlan(plans ?? [], supportCycle),
        [plans, supportCycle]
    )
    const creativesPlan = useMemo(
        () => findCreativesPlan(plans ?? [], creativeTier, creativeFrequency),
        [plans, creativeTier, creativeFrequency]
    )

    const selectedPlans = useMemo(
        () => ({
            [SUBSCRIPTION_LICENSE]: licensePlan,
            [SUBSCRIPTION_SUPPORT]: supportPlan,
            [SUBSCRIPTION_CREATIVE]: creativesPlan,
        }),
        [licensePlan, supportPlan, creativesPlan]
    )

    const selectedPlanIds = useMemo(
        () => ({
            [SUBSCRIPTION_LICENSE]: selectedPlans[SUBSCRIPTION_LICENSE]?.id ?? null,
            [SUBSCRIPTION_SUPPORT]: selectedPlans[SUBSCRIPTION_SUPPORT]?.id ?? null,
            [SUBSCRIPTION_CREATIVE]: selectedPlans[SUBSCRIPTION_CREATIVE]?.id ?? null,
        }),
        [selectedPlans]
    )

    const amounts = useMemo(
        () => ({
            [SUBSCRIPTION_LICENSE]: selectedPlans[SUBSCRIPTION_LICENSE]?.amount ?? null,
            [SUBSCRIPTION_SUPPORT]: selectedPlans[SUBSCRIPTION_SUPPORT]?.amount ?? null,
            [SUBSCRIPTION_CREATIVE]: selectedPlans[SUBSCRIPTION_CREATIVE]?.amount ?? null,
        }),
        [selectedPlans]
    )

    const handleCycleChange = useCallback((planId, cycle) => {
        setBillingCycles((prev) => ({ ...prev, [planId]: cycle }))
    }, [])

    const handleTierChange = useCallback((tier) => {
        setCreativeTier(tier)
    }, [])

    const handleCreativeFrequencyChange = useCallback((frequency) => {
        setCreativeFrequency(frequency)
    }, [])

    const resolvePlanIdForUi = useCallback(
        (uiPlanId) => selectedPlanIds[uiPlanId] ?? null,
        [selectedPlanIds]
    )

    return {
        billingCycles,
        setBillingCycles,
        creativeTier,
        setCreativeTier,
        creativeFrequency,
        setCreativeFrequency,
        handleCycleChange,
        handleTierChange,
        handleCreativeFrequencyChange,
        selectedPlans,
        selectedPlanIds,
        amounts,
        resolvePlanIdForUi,
    }
}
